import { useState, useContext, useCallback, useEffect, useRef } from "react";
import { RefreshCw, Wallet, Loader2 } from "lucide-react";
import { UserContext } from "../context/UserContext";
import api from "../api/axios";

const TYPE_LABELS = {
  deposit: "Deposit",
  withdrawal: "Withdrawal",
  transfer: "Transfer",
  bill: "Bill Payment",
  // crypto_deposit: "Crypto Deposit",
  // crypto_withdrawal: "Crypto Withdrawal",
};

const isInflow = (transaction) => {
  if (transaction?.type === "deposit") return true;
  // if (["deposit", "crypto_deposit"].includes(transaction?.type)) return true;
  if (transaction?.type === "transfer" && transaction?.metadata?.direction === "in") return true;
  return false;
};

export default function UserTransactions() {
  const { transactions, setTransactions, wallet } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const hasAttemptedInitialFetch = useRef(false);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/transactions");
      if (Array.isArray(data)) setTransactions(data.filter((transaction) => !transaction.type?.startsWith("crypto_")));
      else if (Array.isArray(data?.transactions)) setTransactions(data.transactions.filter((transaction) => !transaction.type?.startsWith("crypto_")));
      else setError("Failed to fetch transactions");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Server error. Try again later.");
    } finally {
      setLoading(false);
    }
  }, [setTransactions]);

  useEffect(() => {
    if (!transactions?.length && !hasAttemptedInitialFetch.current) {
      hasAttemptedInitialFetch.current = true;
      fetchTransactions();
    }
  }, [fetchTransactions, transactions?.length]);

  return (
    <div className="mx-auto max-w-6xl space-y-6 text-white">
      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold">Transaction History</h2>
            <p className="mt-1 text-sm text-slate-400">Track all wallet movements and status updates.</p>
          </div>
          <button
            onClick={fetchTransactions}
            disabled={loading}
            aria-busy={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-cyan-300/30 bg-cyan-300/10 px-4 py-2 text-sm font-semibold text-cyan-300 transition hover:bg-cyan-300/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </section>

      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-300/10 text-cyan-300">
            <Wallet size={20} />
          </span>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400">Wallet Balance</p>
            <p className="text-2xl font-bold text-white">₦{Number(wallet.balance || 0).toLocaleString()}</p>
          </div>
        </div>
      </section>

      {loading && (
        <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-10 text-center">
          <Loader2 className="mx-auto animate-spin text-cyan-300" size={30} />
          <p className="mt-3 text-sm text-slate-400">Loading transactions...</p>
        </section>
      )}

      {!loading && error && (
        <section className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
          {error}
        </section>
      )}

      {!loading && !error && transactions.length === 0 && (
        <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-10 text-center text-slate-400">
          No transactions yet.
        </section>
      )}

      {!loading && transactions.length > 0 && (
        <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-4 md:p-6 backdrop-blur-xl">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-separate border-spacing-y-2">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Reference</th>
                  <th className="px-3 py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t._id} className="bg-white/5 text-sm">
                    <td className="rounded-l-xl px-3 py-3 text-white">
                      {TYPE_LABELS[t.type] || t.type?.replaceAll("_", " ")}
                    </td>
                    <td className={`px-3 py-3 font-semibold ${isInflow(t) ? "text-emerald-400" : "text-red-400"}`}>
                      {isInflow(t) ? "+" : "-"}₦{Number(t.amount || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-semibold ${
                          t.status === "success"
                            ? "bg-emerald-500/15 text-emerald-300"
                            : t.status === "pending"
                            ? "bg-amber-500/15 text-amber-300"
                            : "bg-red-500/15 text-red-300"
                        }`}
                      >
                        {t.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-slate-300">{t.reference || "-"}</td>
                    <td className="rounded-r-xl px-3 py-3 text-slate-400">
                      {new Date(t.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden">
            {transactions.map((t) => (
              <div key={t._id} className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-semibold text-white">
                    {TYPE_LABELS[t.type] || t.type?.replaceAll("_", " ")}
                  </p>
                  <span
                    className={`rounded-full px-2 py-1 text-[10px] font-semibold ${
                      t.status === "success"
                        ? "bg-emerald-500/15 text-emerald-300"
                        : t.status === "pending"
                        ? "bg-amber-500/15 text-amber-300"
                        : "bg-red-500/15 text-red-300"
                    }`}
                  >
                    {t.status}
                  </span>
                </div>
                <p className={`mb-1 text-base font-bold ${isInflow(t) ? "text-emerald-400" : "text-red-400"}`}>
                  {isInflow(t) ? "+" : "-"}₦{Number(t.amount || 0).toLocaleString()}
                </p>
                <p className="text-xs text-slate-400">{t.reference || "No reference"}</p>
                <p className="mt-1 text-xs text-slate-500">{new Date(t.createdAt).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
